/**
 * Vorlage „Panieren“.
 *
 * Drei Teller nebeneinander: Mehl, verquirltes Ei, Semmelbrösel. Ein
 * Schnitzel wird mit der Gabel nacheinander durch alle drei gezogen und
 * bekommt dabei sichtbar seine Hülle.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, ellipse, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { plate } from '../parts/kitchen.ts';
import { arrow, counter, GROUND_Y } from '../parts/stage.ts';
import { fork, place } from '../parts/utensils.ts';
import { defineTemplate } from '../template.ts';

/** Teller: Mehl, Ei, Brösel. */
const PLATES = [42, 120, 198] as const;
const PLATE_Y = GROUND_Y - 10;
const PLATE_WIDTH = 72;
/** Schnitzel. */
const PIECE = { width: 46, height: 9 } as const;
/** Höhe über den Tellern beim Wechseln. */
const ABOVE = PLATE_Y - 46;
/** Lage im Teller. */
const IN = PLATE_Y - 7;

/** Zeitplan: hinein, heraus, weiter. */
const TIME = {
  appear: [0.02, 0.08],
  dip1: [0.08, 0.16],
  up1: [0.2, 0.26],
  move2: [0.26, 0.34],
  dip2: [0.34, 0.4],
  up2: [0.44, 0.5],
  move3: [0.5, 0.58],
  dip3: [0.58, 0.64],
  press: [0.64, 0.72],
  vanish: [0.72, 0.78],
  reset: [0.88, 1],
} as const;

/** Hüllen des Schnitzels nacheinander. */
type Coat = 'raw' | 'flour' | 'egg' | 'crumbs';

/** Schnitzel in der Seitenansicht. */
function cutlet(at: Point, coat: Coat): SceneNode {
  const outline = roundedRectPath(
    at.x - PIECE.width / 2,
    at.y - PIECE.height / 2,
    PIECE.width,
    PIECE.height,
    4,
  );
  const crumbs: SceneNode[] =
    coat === 'crumbs'
      ? [-17, -10, -3, 4, 11, 18, -14, 1, 15].map((dx, index) =>
          circle(at.x + dx, at.y + (index > 5 ? 2 : -2), 1.2, styles.flat('deep')),
        )
      : [];
  const shine =
    coat === 'egg'
      ? shape(
          path()
            .move(at.x - 14, at.y - 2)
            .line(at.x + 6, at.y - 2)
            .toString(),
          { stroke: 'paper', strokeWidth: STROKE.detail },
        )
      : null;
  const style = coat === 'flour' ? styles.snow() : styles.solid(coat === 'crumbs' ? 'tint' : 'shade');
  return group(compact([shape(outline, style), group(crumbs, { clip: outline }), shine]));
}

/** Inhalt eines Tellers. */
function plateContents(index: number): SceneNode[] {
  const cx = PLATES[index] as number;
  if (index === 0) {
    return [
      shape(
        path()
          .move(cx - 26, PLATE_Y)
          .quad(cx, PLATE_Y - 10, cx + 26, PLATE_Y)
          .close()
          .toString(),
        styles.snow(),
      ),
    ];
  }
  if (index === 1) {
    return [ellipse(cx, PLATE_Y - 1, 26, 4, styles.solid('shade'))];
  }
  return [
    shape(
      path()
        .move(cx - 26, PLATE_Y)
        .quad(cx, PLATE_Y - 9, cx + 26, PLATE_Y)
        .close()
        .toString(),
      styles.solid('tint'),
    ),
    ...[-14, -6, 3, 11, -2, 8].map((dx, dot) =>
      circle(cx + dx, PLATE_Y - 2 - (dot % 3), 1.1, styles.flat('deep')),
    ),
  ];
}

/** Vorlage „Panieren“. */
export const prepBreadTemplate = defineTemplate({
  id: 'prep.bread',
  group: 'prep',
  title: 'Panieren',
  description:
    'Das Fleisch wird nacheinander in Mehl, verquirltem Ei und Semmelbröseln gewendet. Die Brösel nur leicht andrücken.',
  durationSeconds: 7,
  keywords: [
    'panieren',
    'paniert',
    'panade',
    'panier',
    'paniermehl',
    'in mehl wenden',
    'in semmelbröseln wenden',
    'in mehl, ei',
    'schnitzel',
  ],
  stillTime: 0.38,
  parameters: [],
  scene(_values, t) {
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const appear = ease.outCubic(segment(t, ...TIME.appear));
    const vanish = segment(t, ...TIME.vanish);

    // Weg des Schnitzels.
    let at: Point;
    let coat: Coat = 'raw';
    if (t < TIME.up1[0]) {
      at = { x: PLATES[0], y: lerp(ABOVE, IN, ease.inOutCubic(segment(t, ...TIME.dip1))) };
    } else if (t < TIME.dip2[0]) {
      const up = ease.inOutCubic(segment(t, ...TIME.up1));
      const move = ease.inOutCubic(segment(t, ...TIME.move2));
      at = { x: lerp(PLATES[0], PLATES[1], move), y: lerp(IN, ABOVE, up) };
      coat = 'flour';
    } else if (t < TIME.up2[0]) {
      at = { x: PLATES[1], y: lerp(ABOVE, IN, ease.inOutCubic(segment(t, ...TIME.dip2))) };
      coat = t >= TIME.dip2[1] ? 'egg' : 'flour';
    } else if (t < TIME.dip3[0]) {
      const up = ease.inOutCubic(segment(t, ...TIME.up2));
      const move = ease.inOutCubic(segment(t, ...TIME.move3));
      at = { x: lerp(PLATES[1], PLATES[2], move), y: lerp(IN, ABOVE, up) };
      coat = 'egg';
    } else {
      at = { x: PLATES[2], y: lerp(ABOVE, IN, ease.inOutCubic(segment(t, ...TIME.dip3))) };
      coat = t >= TIME.dip3[1] ? 'crumbs' : 'egg';
    }
    // Beim Andrücken in den Bröseln wippt die Gabel kurz nach unten.
    const press = Math.sin(segment(t, ...TIME.press) * Math.PI * 2) * 1.5;
    const forkVisible = appear * (1 - vanish);
    const piece = t < TIME.dip3[1] ? appear : 1 - reset;

    return compact([
      ...counter(),
      ...PLATES.flatMap((cx, index) => [...plate(cx, PLATE_Y, PLATE_WIDTH), ...plateContents(index)]),
      faded(piece, [cutlet({ x: at.x, y: at.y + Math.max(0, press) }, coat)]),
      faded(forkVisible, [place(at.x + 4, at.y - 2 + Math.max(0, press), 22, fork(), 0.8)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: PLATES[0], y: ABOVE - 14 },
        { x: (PLATES[0] + PLATES[1]) / 2, y: ABOVE - 30 },
        { x: PLATES[1] - 6, y: ABOVE - 12 },
      ]),
      arrow([
        { x: PLATES[1] + 6, y: ABOVE - 14 },
        { x: (PLATES[1] + PLATES[2]) / 2, y: ABOVE - 30 },
        { x: PLATES[2] - 6, y: ABOVE - 12 },
      ]),
    ];
  },
});
